import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Alert from '../../../global/Alert';
import Button from '../../../global/Button';
import InputGroup from '../../../global/InputGroup';
import Spinner from '../../../global/Spinner';
import PinField from '../../../global/PinField';
import { getAsset } from '../../../../util/assets';
import { isValidEmail } from '../../../../util/email';
import { getErrorString } from '../../../../util/normalize-errors';
import { loginSuccess } from '../../../../actions/auth-actions';
import { AUTH_TYPE } from '../../../../constants/auth';
import useEmailPinAuth from '../../hooks/useEmailPinAuth';
import BoxedArrowLink from '../../../global/BoxedArrowLink';

const STEP = {
  EMAIL: 'EMAIL',
  PIN_VERIFY: 'PIN_VERIFY',
};

const propTypes = {
  backToPhoneAuth: PropTypes.func.isRequired,
  // Fires both on login and on register.
  onAuthSuccess: PropTypes.func,
};

const EmailPinAuth = ({ backToPhoneAuth, onAuthSuccess }) => {
  const dispatch = useDispatch();
  const { onEmailAuthenticationRequest, onLoginByEmail } = useEmailPinAuth();
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestPinCode = async () => {
    setLoading(true);
    setError('');

    try {
      await onEmailAuthenticationRequest({ emailAddress: email });
      setStep(STEP.PIN_VERIFY);
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyPinCode = async () => {
    setLoading(true);
    setError('');

    try {
      const {
        data: { accountLoginByEmail: accountLoginByEmailResponse } = {},
      } = await onLoginByEmail({ emailAddress: email, code });

      if (accountLoginByEmailResponse?.success) {
        dispatch(loginSuccess());
        onAuthSuccess?.(AUTH_TYPE.EMAIL);
      } else {
        throw new Error('Could not verify user authenticity');
      }
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Button
        btnStyle="link"
        btnSize="sm"
        className="d-flex my-1"
        onClick={backToPhoneAuth}
      >
        &larr;&nbsp;Back
      </Button>
      {step === STEP.EMAIL && (
        <form
          onSubmit={async event => {
            event.preventDefault();
            await requestPinCode();
          }}
        >
          <p>
            Enter your email, and we&apos;ll send you a code to sign you in.
          </p>
          <InputGroup
            addon={<img alt="" src={getAsset('/shutterbug/icons/email.svg')} />}
            id="email"
            name="email"
            autoFocus
            onChange={event => setEmail(event.target.value)}
            placeholder="example@gmail.com"
            required
            type="email"
            value={email}
            label="Email Address"
            wrapperClassName="mb-2"
          />
          <div className="text-center">
            <Button type="submit" disabled={loading || !isValidEmail(email)}>
              {loading ? (
                <Spinner light size="xs" />
              ) : (
                'Send PIN Code to Verify'
              )}
            </Button>
          </div>
        </form>
      )}

      {step === STEP.PIN_VERIFY && (
        <form
          onSubmit={async event => {
            event.preventDefault();
            await verifyPinCode();
          }}
        >
          <p className="text-center">
            Please enter the code sent to{' '}
            <span className="text-primary">{email}</span>.
          </p>
          <PinField
            id="email-code"
            onChange={setCode}
            onComplete={setCode}
            className="mb-2"
          />
          <div className="text-center">
            <Button type="submit" disabled={loading || code.length < 4}>
              {loading ? <Spinner light size="xs" /> : 'Submit'}
            </Button>
          </div>
          <div className="text-center mt-2">
            <BoxedArrowLink disabled={loading} onClick={requestPinCode}>
              Re&ndash;send code
            </BoxedArrowLink>
          </div>
        </form>
      )}

      {error && (
        <Alert type="danger" className="mt-2">
          {error}
        </Alert>
      )}
    </Fragment>
  );
};

EmailPinAuth.propTypes = propTypes;

export default EmailPinAuth;
